"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { Icon } from "@/components/ui/icon";
import { IconAvatar } from "@/components/ui/icon-avatar";
import { Button, SubmitButton } from "@/components/ui/button";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";
import { customInstance } from "@/lib/api/client";
import { useAuth, useLogout } from "@/features/auth";
import { NEUROSCALE_LINKS } from "@/lib/neuroscale";

interface OrgMembership {
  workos_org_id: string;
  org_id: string | null;
  org_name: string;
  logo_url: string | null;
  role: "admin" | "member";
  is_active_context: boolean;
}

interface OpenInvoice {
  id: string;
  amount_due: number;
  currency: string;
  created: number;
  hosted_invoice_url: string | null;
}

export function SuspendedScreen() {
  const { user, organization, isAdmin } = useAuth();
  const router = useRouter();
  const logout = useLogout();
  const queryClient = useQueryClient();

  const suspensionStatus =
    (organization as Record<string, unknown> | null)?.suspension_status as
      | string
      | null;
  const suspensionReason =
    (organization as Record<string, unknown> | null)?.suspension_reason as
      | string
      | null;
  const isPastDue = suspensionStatus === "past_due";
  const isPlanExpired = suspensionStatus === "plan_expired";
  const isPaused = isPlanExpired && !!suspensionReason?.toLowerCase().includes("paused");

  const memberships = useQuery({
    queryKey: ["org-memberships"],
    queryFn: () =>
      customInstance<{ memberships: OrgMembership[] }>("/api/org/memberships", {
        method: "GET",
      }),
    enabled: !!user,
  });

  const openInvoices = useQuery({
    queryKey: ["billing", "open-invoices"],
    queryFn: () =>
      customInstance<OpenInvoice[]>("/api/billing/open-invoices", {
        method: "GET",
      }),
    enabled: isPastDue && isAdmin && !isPlanExpired,
  });

  const [retrying, setRetrying] = useState(false);

  const otherOrgs = (memberships.data?.memberships ?? []).filter(
    (m) => !m.is_active_context,
  );

  const handleSwitchOrg = async (workosOrgId: string) => {
    try {
      await customInstance("/api/org/switch", {
        method: "POST",
        body: JSON.stringify({ workos_org_id: workosOrgId }),
        headers: { "Content-Type": "application/json" },
      });
      window.location.reload();
    } catch {
      // Silently fail
    }
  };

  const handleRetryPayment = async () => {
    const invoices = openInvoices.data;
    if (!invoices || invoices.length === 0) return;
    setRetrying(true);
    try {
      for (const inv of invoices) {
        await customInstance(`/api/billing/pay-invoice/${inv.id}`, {
          method: "POST",
        });
      }
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      queryClient.invalidateQueries({ queryKey: ["billing"] });
      showToast.success("Payment successful", {
        description: "Your access has been restored.",
      });
      window.location.reload();
    } catch (err) {
      showToast.error("Payment failed", {
        description: getApiErrorMessage(err),
      });
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="flex min-h-svh w-full flex-col bg-surface-primary">
      {/* Header — matches onboarding layout */}
      <header className="flex shrink-0 items-center h-20 px-6">
        <div className="flex-1" />
        <Image
          src="/logos/neuroscale-full.svg"
          alt="Neuroscale AI"
          width={163}
          height={24}
          className="h-6 w-auto"
        />
        <div className="flex flex-1 items-center justify-end">
          <p className="text-[13px] text-contrast/30">
            <span className="hidden sm:inline">Wrong account? </span>
            <button
              onClick={() => logout.mutate(undefined)}
              disabled={logout.isPending}
              className="text-contrast/50 hover:text-contrast/70 transition-colors cursor-pointer disabled:opacity-50"
            >
              Sign out
            </button>
          </p>
        </div>
      </header>

      {/* Body */}
      <main className="flex flex-1 items-center justify-center">
        <div className="flex w-full max-w-md flex-col items-center gap-6 px-6">
          {/* Icon */}
          <div className="flex size-16 items-center justify-center rounded-2xl border border-white/6 bg-white/4">
            <Icon
              name={isPastDue ? "IconCreditCard2" : isPlanExpired ? "IconHourglass" : "IconWarningSign"}
              size={28}
              className={isPastDue ? "text-red-400" : isPlanExpired ? "text-[#FFAF53]" : "text-[#FFAF53]"}
            />
          </div>

          {/* Title + description */}
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-xl font-semibold tracking-tight text-heading">
              {isPastDue
                ? `${organization?.name ?? "Organization"} has a payment issue`
                : isPaused
                  ? `${organization?.name ?? "Organization"}'s plan is paused`
                  : isPlanExpired
                    ? `${organization?.name ?? "Organization"}'s plan has expired`
                    : `${organization?.name ?? "Organization"} is suspended`}
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {isPastDue
                ? isAdmin
                  ? "Your latest payment couldn\u2019t be processed. Update your payment method or retry the payment to restore access for your team."
                  : "Your organization\u2019s latest payment couldn\u2019t be processed. Please contact your admin to resolve this."
                : isPaused
                  ? isAdmin
                    ? "Your plan has been paused by Neuroscale. Contact us to resume your subscription."
                    : "Your organization\u2019s plan has been paused. Please contact your admin for more information."
                : isPlanExpired
                  ? isAdmin
                    ? "Your custom plan has expired. Contact Neuroscale to renew your plan, or sign up for a self-serve subscription."
                    : "Your organization\u2019s plan has expired. Please contact your admin to resolve this."
                  : isAdmin
                    ? suspensionStatus === "admin"
                      ? "Your organization has been suspended. Contact Neuroscale to resolve this."
                      : "Your organization\u2019s subscription has ended. Reactivate your plan to restore access for your team."
                    : suspensionStatus === "admin"
                      ? "Your organization has been suspended. Please contact your admin for more information."
                      : "Your organization\u2019s subscription has ended. Please contact your admin to reactivate the plan."}
            </p>
          </div>

          {/* Actions */}
          <div className="flex w-full flex-col gap-3">
            {isAdmin && isPastDue && (
              <>
                <Button
                  type="button"
                  variant="primary"
                  size="default"
                  className="w-full justify-center"
                  onClick={() => router.push("/settings/plans-billing")}
                >
                  <Icon name="IconCreditCard2" size={16} />
                  Update payment method
                </Button>
                {openInvoices.data && openInvoices.data.length > 0 && (
                  <SubmitButton
                    type="button"
                    variant="secondary"
                    size="default"
                    className="w-full justify-center"
                    loading={retrying}
                    disabled={retrying}
                    onClick={handleRetryPayment}
                  >
                    <Icon name="IconArrowRotateClockwise" size={16} />
                    Retry payment
                  </SubmitButton>
                )}
              </>
            )}

            {isAdmin && isPaused && (
              <Button
                type="button"
                variant="primary"
                size="default"
                className="w-full justify-center"
                asChild
              >
                <a href="mailto:support@neuroscale.ai?subject=Plan%20Resumption">
                  <Icon name="IconEmail1" size={16} />
                  Contact Neuroscale
                </a>
              </Button>
            )}

            {isAdmin && isPlanExpired && !isPaused && (
              <>
                <Button
                  type="button"
                  variant="primary"
                  size="default"
                  className="w-full justify-center"
                  asChild
                >
                  <a href="mailto:support@neuroscale.ai?subject=Plan%20Renewal">
                    <Icon name="IconEmail1" size={16} />
                    Contact Neuroscale
                  </a>
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="default"
                  className="w-full justify-center"
                  onClick={() => router.push("/settings/plans-billing")}
                >
                  <Icon name="IconCreditCard2" size={16} />
                  Browse self-serve plans
                </Button>
              </>
            )}

            {isAdmin && !isPastDue && !isPlanExpired && suspensionStatus === "billing" && (
              <>
                <Button
                  type="button"
                  variant="primary"
                  size="default"
                  className="w-full justify-center"
                  onClick={() => router.push("/settings/plans-billing")}
                >
                  <Icon name="IconCreditCard2" size={16} />
                  Reactivate subscription
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="default"
                  className="w-full justify-center"
                  onClick={() => router.push("/settings/users")}
                >
                  <Icon name="IconUserGroup" size={16} />
                  Manage users
                </Button>
              </>
            )}

            {isAdmin && suspensionStatus === "admin" && (
              <Button
                type="button"
                variant="primary"
                size="default"
                className="w-full justify-center"
                asChild
              >
                <a href="mailto:support@neuroscale.ai?subject=Organization%20Suspension">
                  <Icon name="IconEmail1" size={16} />
                  Contact Neuroscale
                </a>
              </Button>
            )}

            {otherOrgs.length > 0 && (
              <>
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-white/6" />
                  <span className="text-xs text-muted-foreground">
                    or switch organization
                  </span>
                  <div className="h-px flex-1 bg-white/6" />
                </div>

                <div className="flex flex-col gap-2">
                  {otherOrgs.map((org) => (
                    <button
                      key={org.workos_org_id}
                      type="button"
                      onClick={() => handleSwitchOrg(org.workos_org_id)}
                      className="flex items-center gap-3 rounded-lg border border-white/6 bg-white/2 px-3 py-2.5 text-left transition-colors hover:bg-white/5"
                    >
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md border border-white/6 bg-white/4">
                        <IconAvatar
                          src={org.logo_url}
                          size={20}
                          alt={org.org_name}
                          className="rounded-sm bg-transparent"
                          icon={
                            <Icon
                              name="IconBuildings"
                              className="text-muted-foreground"
                            />
                          }
                        />
                      </div>
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium text-heading">
                          {org.org_name}
                        </span>
                        <span className="text-xs capitalize text-muted-foreground">
                          {org.role}
                        </span>
                      </div>
                      <Icon
                        name="IconArrowRight"
                        size={14}
                        className="ml-auto shrink-0 text-muted-foreground"
                      />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Footer — matches onboarding layout */}
      <footer className="flex shrink-0 flex-wrap items-center justify-center gap-x-4 gap-y-1 py-5 text-[13px] text-contrast/40">
        <span>&copy; {new Date().getFullYear()} Neuroscale AI, Inc.</span>
        <Link
          href={NEUROSCALE_LINKS.terms}
          className="transition-colors hover:text-contrast/60"
        >
          Terms of Service
        </Link>
        <Link
          href={NEUROSCALE_LINKS.privacy}
          className="transition-colors hover:text-contrast/60"
        >
          Privacy Policy
        </Link>
      </footer>
    </div>
  );
}
